import { useCallback, useEffect, useRef, useState } from 'react';
import {
  formatPence,
  type AdminQuizView,
  type TicketSearchHit,
  type AdminPlayerRow,
} from '@quiz/shared';
import { api, ApiError } from '../lib/api.js';
import { QuizEditor } from './QuizEditor.js';
import { QuestionBank } from './QuestionBank.js';

const TOKEN_KEY = 'fast-quiz/admin-token';

interface QuizSummary {
  id: string;
  name: string;
  roundCount: number;
  questionCount: number;
}

/**
 * The builder. This is the one screen that is not mobile-first: it is used at a
 * desk the week before, not in the pub on the night.
 */
export function AdminApp() {
  const [token, setToken] = useState<string>(() => localStorage.getItem(TOKEN_KEY) ?? '');
  const [authed, setAuthed] = useState(false);
  const [quizzes, setQuizzes] = useState<QuizSummary[]>([]);
  const [quiz, setQuiz] = useState<AdminQuizView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [seeded, setSeeded] = useState<SeedResult | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [section, setSection] = useState<'quizzes' | 'people'>('quizzes');

  async function loadQuizzes(t = token) {
    try {
      const res = await api<{ quizzes: QuizSummary[] }>('/api/admin/quizzes', { token: t });
      setQuizzes(res.quizzes);
      setAuthed(true);
      localStorage.setItem(TOKEN_KEY, t);
      setError(null);
    } catch (err) {
      setAuthed(false);
      setError(err instanceof ApiError ? err.message : 'Could not reach the server.');
    }
  }

  /**
   * Once, on mount, with whatever token was restored from localStorage.
   *
   * It reads storage rather than the `token` state on purpose. `token` is also
   * the value of the sign-in field, so depending on it would re-fire this on
   * every keystroke somebody types while entering one -- and depending on it
   * *without* declaring it is the shape of mistake that took the door tab down
   * this week. Reading the source of truth directly means there is no
   * dependency to get wrong.
   */
  const bootRef = useRef(false);
  useEffect(() => {
    if (bootRef.current) return;
    bootRef.current = true;
    let saved: string | null = null;
    try {
      saved = localStorage.getItem(TOKEN_KEY);
    } catch {
      // Private browsing or blocked storage: they sign in by hand, which is fine.
    }
    if (saved) void loadQuizzes(saved);
  });

  if (!authed) {
    // A four-digit entry is somebody reaching for their staff PIN. Say so before
    // they try it three more times: this screen wants a different credential
    // entirely, and "Admin token required" does not tell them that.
    const looksLikeAPin = /^\d{4,6}$/.test(token.trim());

    return (
      <div className="min-h-dvh bg-ink p-8 text-chalk">
        <div className="mx-auto max-w-md space-y-4 pt-20">
          <h1 className="text-2xl font-bold">Quiz builder</h1>
          <p className="text-sm text-muted">
            This screen is for writing quizzes, not for running one. It needs the{' '}
            <b>admin token</b> &mdash; a long random string, not a PIN.
          </p>
          <input
            value={token}
            onChange={(e) => setToken(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void loadQuizzes()}
            placeholder="Admin token"
            type="password"
            autoComplete="off"
            className="w-full rounded-lg bg-ink-2 px-4 py-3 outline-none ring-1 ring-ink-3 focus:ring-sky"
          />

          {looksLikeAPin && (
            <div className="rounded-lg bg-flag/15 p-3 text-sm text-flag">
              That looks like a staff PIN. Hosts and managers sign in at{' '}
              <a className="underline" href="/staff">/staff</a>, and the printable
              run sheet is at <a className="underline" href="/print">/print</a>.
            </div>
          )}
          {error && !looksLikeAPin && <p className="text-sm text-stop">{error}</p>}

          <button
            onClick={() => void loadQuizzes()}
            className="min-h-12 w-full rounded-lg bg-sky font-semibold text-ink"
          >
            Open
          </button>

          <details className="rounded-lg bg-ink-2 p-3 text-sm text-muted">
            <summary className="cursor-pointer">Where do I find the admin token?</summary>
            <p className="mt-2">
              It is an environment variable on the server, generated when the app
              was deployed. On Render: <b>Projects</b> &rarr; your web service
              &rarr; <b>Environment</b> &rarr; copy the value of{' '}
              <span className="font-mono">ADMIN_TOKEN</span>.
            </p>
            <p className="mt-2">
              Running locally, it is <span className="font-mono">ADMIN_TOKEN</span>{' '}
              in your <span className="font-mono">.env</span> file.
            </p>
          </details>
        </div>
      </div>
    );
  }

  /**
   * A fresh deploy has an empty database: no quiz, no event, no staff PINs. The
   * usual fix is a seed script over a shell on the server, which assumes a
   * hosting plan that may not have one. This is the same thing, as a button.
   */
  async function seed() {
    setSeeding(true);
    setError(null);
    try {
      setSeeded(await api<SeedResult>('/api/admin/seed', { method: 'POST', token }));
      await loadQuizzes();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the starter quiz.');
    } finally {
      setSeeding(false);
    }
  }

  return (
    <div className="min-h-dvh bg-ink text-chalk">
      <header className="border-b border-ink-2 px-6 py-4">
        <div className="mx-auto flex max-w-7xl items-center gap-4">
          <h1 className="text-lg font-bold">Quiz builder</h1>
          <select
            value={quiz?.id ?? ''}
            onChange={async (e) => {
              const id = e.target.value;
              if (!id) return setQuiz(null);
              try {
                setQuiz(await api<AdminQuizView>(`/api/admin/quizzes/${id}`, { token }));
                setError(null);
              } catch (err) {
                setError(err instanceof ApiError ? err.message : 'Could not open that quiz.');
              }
            }}
            className="rounded-lg bg-ink-2 px-3 py-2 text-sm outline-none"
          >
            <option value="">Choose a quiz…</option>
            {quizzes.map((q) => (
              <option key={q.id} value={q.id}>
                {q.name} ({q.roundCount} rounds, {q.questionCount} questions)
              </option>
            ))}
          </select>
          <button
            onClick={async () => {
              const name = prompt('Name this quiz (e.g. "Thursday 12 March")');
              if (!name) return;
              try {
                const created = await api<AdminQuizView>('/api/admin/quizzes', {
                  method: 'POST',
                  token,
                  body: { name },
                });
                await loadQuizzes();
                setQuiz(created);
                setError(null);
              } catch (err) {
                setError(err instanceof ApiError ? err.message : 'Could not create that quiz.');
              }
            }}
            className="rounded-lg bg-ink-2 px-3 py-2 text-sm hover:bg-ink-3"
          >
            + New quiz
          </button>
        </div>
      </header>

      {/* Building a quiz and looking after the people who played it are two
          different jobs, done at different times, by somebody in a different
          frame of mind. They were sharing one scrolling page. */}
      <nav className="mx-auto mt-4 flex max-w-7xl gap-2 px-6">
        {(
          [
            ['quizzes', 'Quizzes & events'],
            ['people', 'Players & refunds'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setSection(id)}
            className={`rounded-lg px-4 py-2 text-sm font-semibold ${
              section === id ? 'bg-sky text-ink' : 'bg-ink-2 text-muted hover:bg-ink-3'
            }`}
          >
            {label}
          </button>
        ))}
      </nav>

      {error && (
        <p
          className="mx-auto mt-4 max-w-7xl rounded-xl bg-stop/15 px-4 py-3 text-sm text-stop"
          role="alert"
        >
          {error}
        </p>
      )}

      {section === 'people' ? (
        <main className="mx-auto max-w-7xl space-y-6 p-6">
          <PlayersPanel token={token} />
          <RefundsPanel token={token} />
        </main>
      ) : (

      <main className="mx-auto grid max-w-7xl gap-6 p-6 lg:grid-cols-[1fr_24rem]">
        <div className="space-y-6">
          {seeded ? (
            <SeedResultCard result={seeded} onDismiss={() => setSeeded(null)} />
          ) : quiz ? (
            <QuizEditor token={token} quiz={quiz} onChange={setQuiz} />
          ) : quizzes.length === 0 ? (
            <EmptyDatabase onSeed={() => void seed()} busy={seeding} error={error} />
          ) : (
            <p className="text-muted">Pick a quiz above, or make a new one.</p>
          )}
          {quizzes.length > 0 && <EventsPanel token={token} />}
        </div>
        <QuestionBank
          token={token}
          onAdd={
            // No add target while a host is part-way through this quiz: the
            // server refuses, and offering the control anyway is just a trap.
            quiz && !quiz.inSession
              ? async (questionId, roundId) => {
                  try {
                    setQuiz(
                      await api<AdminQuizView>(`/api/admin/rounds/${roundId}/questions`, {
                        method: 'POST',
                        token,
                        body: { questionId },
                      }),
                    );
                    setError(null);
                  } catch (err) {
                    // Most often "that question is already in this round".
                    setError(err instanceof ApiError ? err.message : 'Could not add that question.');
                  }
                }
              : undefined
          }
          rounds={
            quiz && !quiz.inSession
              ? quiz.rounds.map((r) => ({ id: r.id, title: r.title }))
              : []
          }
        />
      </main>
      )}
    </div>
  );
}

interface SeedResult {
  quizName: string;
  roundCount: number;
  questionCount: number;
  eventName: string;
  joinMode: 'TICKET' | 'CODE' | 'OPEN';
  joinCode: string | null;
  hostPin: string;
  managerPin: string;
}

/** What you see on a brand-new deployment, before anything exists. */
function EmptyDatabase({
  onSeed,
  busy,
  error,
}: {
  onSeed: () => void;
  busy: boolean;
  error: string | null;
}) {
  return (
    <div className="rounded-xl bg-ink-2 p-6">
      <h2 className="text-xl font-bold">Nothing here yet</h2>
      <p className="mt-2 max-w-prose text-muted">
        This database is empty, so there is no quiz to run and no staff PINs to
        sign in with. Start with the ready-made quiz &mdash; it creates a free
        practice event that anybody can join with no code, which is the one to
        run first anyway.
      </p>
      <button
        disabled={busy}
        onClick={onSeed}
        className="mt-5 min-h-12 rounded-lg bg-go px-6 font-semibold text-ink disabled:opacity-40"
      >
        {busy ? 'Creating…' : 'Create the starter quiz'}
      </button>
      {error && <p className="mt-3 text-sm text-stop">{error}</p>}
      <p className="mt-5 max-w-prose text-sm text-muted">
        Or press <b>+ New quiz</b> above and write your own from scratch.
      </p>
    </div>
  );
}

function SeedResultCard({ result, onDismiss }: { result: SeedResult; onDismiss: () => void }) {
  return (
    <div className="rounded-xl bg-go/15 p-6">
      <h2 className="text-xl font-bold text-go">Ready to run</h2>
      <p className="mt-2 text-muted">
        {result.quizName} &mdash; {result.roundCount} rounds, {result.questionCount} questions.
      </p>

      <div className="mt-5 space-y-2">
        <p>
          <span className="text-muted">Event</span> {result.eventName}
        </p>
        <p>
          <span className="text-muted">Players join by</span>{' '}
          {result.joinMode === 'OPEN' ? (
            <b>opening the site and tapping join &mdash; no code</b>
          ) : result.joinMode === 'CODE' ? (
            <>
              the shared code <b className="font-mono">{result.joinCode}</b>
            </>
          ) : (
            'a ticket code issued at the door'
          )}
        </p>
      </div>

      {/* Shown once and only once: they are hashed the moment they are stored. */}
      <div className="mt-5 rounded-lg bg-ink p-4">
        <p className="text-xs uppercase tracking-wide text-flag">
          Write these down &mdash; they cannot be shown again
        </p>
        <p className="mt-2 text-lg">
          Host PIN <b className="font-mono">{result.hostPin}</b>
          <span className="mx-3 text-muted">&middot;</span>
          Manager PIN <b className="font-mono">{result.managerPin}</b>
        </p>
        <p className="mt-2 text-sm text-muted">
          Staff sign in with these at <span className="font-mono">/staff</span>.
        </p>
      </div>

      <button onClick={onDismiss} className="mt-5 text-sm text-muted underline">
        Done
      </button>
    </div>
  );
}

interface EventRow {
  id: string;
  name: string;
  quizName: string;
  status: 'SETUP' | 'LIVE' | 'ENDED';
  joinMode: 'TICKET' | 'CODE' | 'OPEN';
  joinCode: string | null;
  entryFeePence: number;
  scheduledFor: string | null;
  playerCount: number;
  staff: { id: string; displayName: string; role: 'HOST' | 'MANAGER' }[];
}

/** yyyy-MM-ddTHH:mm for a datetime-local input, in the browser's own zone. */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Events and who can run them.
 *
 * Mostly here for one question: "we have lost the staff PINs". They are
 * scrypt-hashed on write, so there is genuinely no way to show the old ones --
 * you add a new person and get a new PIN. This is where you do that.
 */
function EventsPanel({ token }: { token: string }) {
  const [events, setEvents] = useState<EventRow[]>([]);
  const [newPin, setNewPin] = useState<{ displayName: string; role: string; pin: string } | null>(
    null,
  );
  const [editing, setEditing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api<{ events: EventRow[] }>('/api/admin/events', { token });
      setEvents(res.events);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load events.');
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(
    path: string,
    method: 'POST' | 'PATCH' | 'DELETE',
    body?: unknown,
  ): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      await api(path, { method, token, body });
      await load();
      return true;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  /**
   * Deleting a night that took money is possible, but only on purpose.
   *
   * The server refuses first and names what would be destroyed; only then are
   * you asked to type the event's name back. That second step is the whole
   * safeguard -- everything cascades off Event, including the audit log that
   * would otherwise record the deletion, so there is no undo and nothing left
   * afterwards to say it happened.
   */
  async function deleteEvent(e: EventRow) {
    if (await act(`/api/admin/events/${e.id}`, 'DELETE')) return;

    const typed = prompt(
      `"${e.name}" has players, tickets or answers attached, and deleting it destroys that ` +
        `record for good. If you are sure, type the event name exactly to confirm:`,
    );
    if (typed === null) return;
    if (typed !== e.name) {
      setError('That did not match the event name, so nothing was deleted.');
      return;
    }
    await act(`/api/admin/events/${e.id}?confirmName=${encodeURIComponent(e.name)}`, 'DELETE');
  }

  async function addStaff(eventId: string, role: 'HOST' | 'MANAGER') {
    const displayName = prompt(
      `Name for the new ${role.toLowerCase()} (it appears in the audit log)`,
      role === 'HOST' ? 'Host' : 'Manager',
    );
    if (!displayName) return;
    setBusy(true);
    setError(null);
    try {
      setNewPin(
        await api(`/api/admin/events/${eventId}/staff`, {
          method: 'POST',
          token,
          body: { displayName, role },
        }),
      );
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add them.');
    } finally {
      setBusy(false);
    }
  }

  if (events.length === 0) return null;

  return (
    <section className="rounded-xl bg-ink-2 p-4">
      <h2 className="font-bold">Events &amp; staff</h2>
      <p className="mt-1 text-sm text-muted">
        PINs are stored hashed and cannot be shown again. Lost them? Add a new
        person &mdash; that is what this is for.
      </p>

      {newPin && (
        <div className="mt-4 rounded-lg bg-go/15 p-4">
          <p className="text-xs uppercase tracking-wide text-flag">
            Write this down &mdash; it cannot be shown again
          </p>
          <p className="mt-2 text-lg">
            {newPin.displayName} ({newPin.role.toLowerCase()}) &mdash; PIN{' '}
            <b className="font-mono text-2xl">{newPin.pin}</b>
          </p>
          <p className="mt-1 text-sm text-muted">They sign in at /staff with that.</p>
          <button onClick={() => setNewPin(null)} className="mt-2 text-sm text-muted underline">
            Done
          </button>
        </div>
      )}

      {error && <p className="mt-3 text-sm text-stop">{error}</p>}

      <ul className="mt-4 space-y-3">
        {events.map((e) => (
          <li key={e.id} className="rounded-lg bg-ink p-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold">{e.name}</span>
              <span
                className={`rounded-full px-2 py-0.5 text-xs ${
                  e.status === 'LIVE'
                    ? 'bg-go/20 text-go'
                    : e.status === 'ENDED'
                      ? 'bg-ink-3 text-muted'
                      : 'bg-sky/20 text-sky'
                }`}
              >
                {e.status.toLowerCase()}
              </span>
              <span className="text-xs text-muted">
                {e.joinMode === 'OPEN'
                  ? 'practice · no code'
                  : e.joinMode === 'CODE'
                    ? `shared code ${e.joinCode}`
                    : `£${(e.entryFeePence / 100).toFixed(2)} · door codes`}
                {' · '}
                {e.playerCount} played
              </span>
            </div>

            <p className="mt-2 text-xs text-muted">
              {e.staff.length > 0
                ? e.staff.map((s) => `${s.displayName} (${s.role.toLowerCase()})`).join(', ')
                : 'nobody can sign in to this event'}
            </p>

            {editing === e.id ? (
              <EditEvent
                event={e}
                busy={busy}
                onCancel={() => setEditing(null)}
                onSave={async (body) => {
                  if (await act(`/api/admin/events/${e.id}`, 'PATCH', body)) setEditing(null);
                }}
              />
            ) : (
              <div className="mt-2 flex flex-wrap gap-3 text-xs">
                <button
                  disabled={busy}
                  onClick={() => setEditing(e.id)}
                  className="text-sky underline disabled:opacity-40"
                >
                  Edit
                </button>
                {/* Deep-links the print page at THIS night, so next Thursday's
                    paperwork can be prepared without its manager PIN having
                    been handed to anybody yet. */}
                <a
                  href={`/print?event=${e.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sky underline"
                >
                  Print
                </a>
                <button
                  disabled={busy}
                  onClick={() => void addStaff(e.id, 'HOST')}
                  className="text-sky underline disabled:opacity-40"
                >
                  + host PIN
                </button>
                <button
                  disabled={busy}
                  onClick={() => void addStaff(e.id, 'MANAGER')}
                  className="text-sky underline disabled:opacity-40"
                >
                  + manager PIN
                </button>

                {e.status !== 'ENDED' && (
                  <button
                    disabled={busy}
                    onClick={() =>
                      confirm(`End "${e.name}"? Players stop, and the results are kept.`) &&
                      void act(`/api/admin/events/${e.id}/end`, 'POST')
                    }
                    className="ml-auto text-flag underline disabled:opacity-40"
                  >
                    End it
                  </button>
                )}

                <button
                  disabled={busy}
                  onClick={() =>
                    confirm(`Delete "${e.name}" completely?`) &&
                    void deleteEvent(e)
                  }
                  className={`text-stop underline disabled:opacity-40 ${e.status === 'ENDED' ? 'ml-auto' : ''}`}
                >
                  Delete
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Name and date are always editable. The fee and the way in are not, once codes
 * have been sold -- the server refuses, and there is no point offering a control
 * that will be turned down.
 */
function EditEvent({
  event,
  busy,
  onCancel,
  onSave,
}: {
  event: EventRow;
  busy: boolean;
  onCancel: () => void;
  onSave: (body: Record<string, unknown>) => Promise<void>;
}) {
  const [name, setName] = useState(event.name);
  const [when, setWhen] = useState(toLocalInput(event.scheduledFor));
  const [feePence, setFeePence] = useState(event.entryFeePence);
  const settled = event.playerCount > 0;
  // A paid night can change its price; it cannot become free once codes exist,
  // because free and paid are different machines rather than different numbers.
  const canPrice = event.joinMode === 'TICKET' && event.entryFeePence > 0;

  return (
    <div className="mt-3 space-y-2 rounded-lg bg-ink-2 p-3">
      <label className="block text-xs text-muted">
        Name
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1 w-full rounded bg-ink px-2 py-1.5 text-sm outline-none"
        />
      </label>
      <label className="block text-xs text-muted">
        When (leave empty for no date)
        <input
          type="datetime-local"
          value={when}
          onChange={(e) => setWhen(e.target.value)}
          className="mt-1 w-full rounded bg-ink px-2 py-1.5 text-sm outline-none"
        />
      </label>
      {canPrice && (
        <label className="block text-xs text-muted">
          Entry fee (pence)
          <input
            type="number"
            min={1}
            step={1}
            value={feePence}
            onChange={(e) => setFeePence(Math.max(1, Math.round(Number(e.target.value))))}
            className="mt-1 w-full rounded bg-ink px-2 py-1.5 text-sm tabular-nums outline-none"
          />
          <span className="mt-1 block">
            £{(feePence / 100).toFixed(2)} per person.
            {settled
              ? ' Anyone who has already paid keeps what they paid; only the next person sees this.'
              : ''}
          </span>
        </label>
      )}
      {settled && (
        <p className="text-xs text-muted">
          People have joined, so the way in is fixed. The price can still change.
        </p>
      )}
      <div className="flex gap-2 pt-1">
        <button
          disabled={busy || !name.trim()}
          onClick={() =>
            void onSave({
              name: name.trim(),
              scheduledFor: when ? new Date(when).toISOString() : null,
              ...(canPrice && feePence !== event.entryFeePence
                ? { entryFeePence: feePence }
                : {}),
            })
          }
          className="rounded bg-go px-4 py-1.5 text-xs font-semibold text-ink disabled:opacity-40"
        >
          Save
        </button>
        <button onClick={onCancel} className="rounded bg-ink px-4 py-1.5 text-xs text-muted">
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * Refunds across history.
 *
 * Staff are scoped to one event, so the door tab cannot answer "I played a few
 * weeks ago and I want my money back" -- a manager can find the ticket there
 * but not refund another night's takings with it. This is where that happens,
 * on the credential that already creates events. Every refund writes an audit
 * entry naming `admin`.
 */
function RefundsPanel({ token }: { token: string }) {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<TicketSearchHit[] | null>(null);
  const [confirming, setConfirming] = useState<TicketSearchHit | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const search = useCallback(
    async (term: string) => {
      if (term.trim().length < 2) return setHits(null);
      try {
        const r = await api<{ hits: TicketSearchHit[] }>(
          `/api/admin/tickets/search?q=${encodeURIComponent(term.trim())}`,
          { token },
        );
        setHits(r.hits);
        setError(null);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Could not search.');
      }
    },
    [token],
  );

  useEffect(() => {
    const t = setTimeout(() => void search(query), 250);
    return () => clearTimeout(t);
  }, [query, search]);

  async function refund(hit: TicketSearchHit) {
    setBusy(true);
    try {
      await api('/api/admin/refund', {
        method: 'POST',
        token,
        body: { ticketId: hit.ticket.id },
      });
      setDone(`Refunded ${formatPence(hit.ticket.amountPence - (hit.ticket.refundedPence ?? 0))} on ${hit.ticket.code}.`);
      setConfirming(null);
      setError(null);
      await search(query);
    } catch (err) {
      // The provider's own reason arrives here -- show it verbatim.
      setError(err instanceof ApiError ? err.message : 'The refund did not go through.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rounded-2xl bg-ink-2 p-5">
      <h2 className="text-lg font-bold">Refunds</h2>
      <p className="mt-1 text-sm text-muted">
        Any night. Search by the player&rsquo;s name or their code.
      </p>

      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Name or code…"
        className="mt-3 w-full rounded-lg bg-ink px-3 py-2 outline-none ring-1 ring-ink-3 focus:ring-sky"
      />

      {error && (
        <p className="mt-3 rounded-lg bg-stop/15 px-3 py-2 text-sm text-stop" role="alert">
          {error}
        </p>
      )}
      {done && (
        <p className="mt-3 rounded-lg bg-go/15 px-3 py-2 text-sm text-go">
          {done}{' '}
          <button onClick={() => setDone(null)} className="underline">
            Dismiss
          </button>
        </p>
      )}

      {hits !== null && hits.length === 0 && (
        <p className="mt-3 text-sm text-muted">Nothing matching that.</p>
      )}

      <RefundLog token={token} reloadKey={done} />

      {hits !== null && hits.length > 0 && (
        <ul className="mt-3 space-y-2">
          {hits.map((h) => {
            const t = h.ticket;
            const outstanding = t.amountPence - (t.refundedPence ?? 0);
            const isConfirming = confirming?.ticket.id === t.id;
            return (
              <li key={t.id} className="rounded-lg bg-ink p-3 text-sm">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-mono">{t.code}</span>
                  <span className="text-muted">
                    {t.source.toLowerCase()} · {formatPence(t.amountPence)}
                  </span>
                </div>
                <p className="mt-1 text-muted">
                  {h.playerName ?? 'not used'} · {h.eventName}
                  {h.eventScheduledFor
                    ? ` · ${new Date(h.eventScheduledFor).toLocaleDateString()}`
                    : ''}
                </p>
                {t.refundedPence ? (
                  <p className="mt-1 text-xs text-muted">
                    {formatPence(t.refundedPence)} already refunded
                  </p>
                ) : null}

                {outstanding <= 0 ? null : isConfirming ? (
                  <div className="mt-2 rounded-lg bg-stop/10 p-2">
                    <p className="text-xs text-stop">
                      {t.source === 'CARD'
                        ? `Refund ${formatPence(outstanding)} to the card at SumUp now? This cannot be undone from here.`
                        : `Record a ${formatPence(outstanding)} cash refund? Hand the money back yourself.`}
                    </p>
                    <div className="mt-2 flex gap-2">
                      <button
                        disabled={busy}
                        onClick={() => void refund(h)}
                        className="rounded-lg bg-stop px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
                      >
                        {busy ? 'Refunding…' : `Yes, refund ${formatPence(outstanding)}`}
                      </button>
                      <button
                        disabled={busy}
                        onClick={() => setConfirming(null)}
                        className="rounded-lg bg-ink-3 px-3 py-1.5 text-xs disabled:opacity-40"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirming(h)}
                    className="mt-2 text-sm text-stop underline"
                  >
                    Refund {formatPence(outstanding)}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

interface RefundLogEntry {
  id: string;
  action: 'REFUND_ATTEMPT' | 'REFUND' | 'REFUND_FAILED' | string;
  eventName: string;
  ticketId: string | null;
  actor: string | null;
  detail: { code?: string; amountPence?: number; reason?: string; by?: string } | null;
  createdAt: string;
}

/**
 * Every refund that was ever started, whatever became of it.
 *
 * Three actions tell the whole story. REFUND_ATTEMPT is written before the
 * money can move, then either REFUND or REFUND_FAILED lands beside it. So an
 * attempt sitting on its own is the one worth looking at: it means something
 * died between asking the provider and recording the answer, and somebody
 * should check the provider's dashboard by hand.
 */
function RefundLog({ token, reloadKey }: { token: string; reloadKey: string | null }) {
  const [entries, setEntries] = useState<RefundLogEntry[] | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    api<{ entries: RefundLogEntry[] }>('/api/admin/refunds?limit=100', { token })
      .then((r) => setEntries(r.entries))
      .catch(() => setEntries([]));
  }, [token, open, reloadKey]);

  const tone: Record<string, string> = {
    REFUND: 'text-go',
    REFUND_FAILED: 'text-stop',
    REFUND_ATTEMPT: 'text-muted',
  };
  const label: Record<string, string> = {
    REFUND: 'refunded',
    REFUND_FAILED: 'failed',
    REFUND_ATTEMPT: 'attempted',
  };

  return (
    <div className="mt-5 border-t border-ink pt-4">
      <button onClick={() => setOpen((o) => !o)} className="text-sm text-sky underline">
        {open ? 'Hide' : 'Show'} refund log
      </button>

      {open && entries === null && <p className="mt-2 text-sm text-muted">Loading…</p>}
      {open && entries?.length === 0 && (
        <p className="mt-2 text-sm text-muted">No refunds have been attempted.</p>
      )}

      {open && entries && entries.length > 0 && (
        <ul className="mt-3 space-y-1.5 text-xs">
          {entries.map((e) => (
            <li key={e.id} className="flex flex-wrap items-baseline gap-x-2">
              <span className={`font-semibold ${tone[e.action] ?? 'text-muted'}`}>
                {label[e.action] ?? e.action}
              </span>
              <span className="font-mono">{e.detail?.code ?? e.ticketId?.slice(0, 8) ?? '—'}</span>
              {typeof e.detail?.amountPence === 'number' && (
                <span>{formatPence(e.detail.amountPence)}</span>
              )}
              <span className="text-muted">
                {e.eventName} · {new Date(e.createdAt).toLocaleString()} ·{' '}
                {e.actor ?? e.detail?.by ?? 'admin'}
              </span>
              {e.detail?.reason && (
                <span className="w-full text-stop">{e.detail.reason}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

type RosterSort = 'player' | 'night' | 'score' | 'ticket' | 'paid' | 'joined';

interface PlayerPage {
  players: AdminPlayerRow[];
  total: number;
  page: number;
  limit: number;
  pages: number;
  sort: RosterSort;
  dir: 'asc' | 'desc';
}

const COLUMNS: { key: RosterSort; label: string; align?: 'right' }[] = [
  { key: 'player', label: 'Player' },
  { key: 'night', label: 'Night' },
  { key: 'score', label: 'Score', align: 'right' },
  { key: 'ticket', label: 'Ticket' },
  { key: 'paid', label: 'Paid', align: 'right' },
  { key: 'joined', label: 'Joined' },
];

/**
 * Everyone who has ever joined anything.
 *
 * Deliberately unfiltered: still playing, ejected, refunded, from a night two
 * months ago. The screen exists to answer "who was this and what happened to
 * them", and hiding the awkward rows is how you lose the ability to answer it.
 * Ejected players are struck through rather than dropped, with the reason the
 * manager had to type at the time.
 */
function PlayersPanel({ token }: { token: string }) {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sort, setSort] = useState<RosterSort>('joined');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [data, setData] = useState<PlayerPage | null>(null);
  const [error, setError] = useState<string | null>(null);

  // A new search, or a bigger page, starts at the beginning; staying on page 7
  // of the old results shows an empty table and reads as "no matches".
  useEffect(() => {
    setPage(1);
  }, [query, perPage, sort, dir]);

  useEffect(() => {
    const t = setTimeout(() => {
      api<PlayerPage>(
        `/api/admin/players?q=${encodeURIComponent(query.trim())}&page=${page}` +
          `&limit=${perPage}&sort=${sort}&dir=${dir}`,
        { token },
      )
        .then((r) => {
          setData(r);
          setError(null);
        })
        .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load.'));
    }, 250);
    return () => clearTimeout(t);
  }, [token, query, page, perPage, sort, dir]);

  /**
   * Clicking the column you are already on flips the direction; clicking a new
   * one starts it descending, because on every column here the interesting end
   * is the top -- the highest score, the most recent night, the biggest payment.
   * Except the two name columns, where A-Z is what anybody means.
   */
  function sortBy(key: RosterSort) {
    if (key === sort) {
      setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSort(key);
    setDir(key === 'player' || key === 'night' ? 'asc' : 'desc');
  }

  return (
    <section className="rounded-2xl bg-ink-2 p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-lg font-bold">Players</h2>
        {data && (
          <p className="text-sm text-muted">
            {data.total} {data.total === 1 ? 'player' : 'players'}
            {query.trim().length >= 2 ? ' matching' : ' in all'}
          </p>
        )}
      </div>
      <p className="mt-1 text-sm text-muted">
        Every night, everyone &mdash; including ejected and refunded. Search a name, a code, or a
        night.
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Name, ticket code, or event…"
          className="min-w-[16rem] flex-1 rounded-lg bg-ink px-3 py-2 outline-none ring-1 ring-ink-3 focus:ring-sky"
        />
        <label className="flex items-center gap-2 text-sm text-muted">
          Show
          <select
            value={perPage}
            onChange={(e) => setPerPage(Number(e.target.value))}
            className="rounded-lg bg-ink px-2 py-2 text-sm outline-none ring-1 ring-ink-3 focus:ring-sky"
          >
            {[10, 20, 50].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          per page
        </label>
      </div>

      {error && (
        <p className="mt-3 rounded-lg bg-stop/15 px-3 py-2 text-sm text-stop" role="alert">
          {error}
        </p>
      )}

      {data && data.players.length === 0 && (
        <p className="mt-4 text-sm text-muted">Nobody matching that.</p>
      )}

      {data && data.players.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[46rem] border-collapse text-sm">
            <thead>
              <tr className="border-b border-ink text-left text-xs uppercase tracking-wide text-muted">
                {COLUMNS.map((c, i) => (
                  <th
                    key={c.key}
                    aria-sort={
                      sort === c.key ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'
                    }
                    className={`py-2 font-medium ${i < COLUMNS.length - 1 ? 'pr-3' : ''} ${
                      c.align === 'right' ? 'text-right' : ''
                    }`}
                  >
                    <button
                      onClick={() => sortBy(c.key)}
                      className={`inline-flex items-center gap-1 uppercase tracking-wide hover:text-chalk ${
                        sort === c.key ? 'text-chalk' : ''
                      }`}
                    >
                      {c.label}
                      <span aria-hidden className={sort === c.key ? '' : 'opacity-30'}>
                        {sort === c.key ? (dir === 'asc' ? '▲' : '▼') : '▾'}
                      </span>
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.players.map((p) => (
                <tr
                  key={p.id}
                  className={`border-b border-ink/60 ${p.isWinner ? 'bg-go/10' : ''}`}
                >
                  <td className="py-2 pr-3">
                    <span className={p.removedAt ? 'text-muted line-through' : 'font-semibold'}>
                      {p.callSign}
                    </span>
                    {p.isWinner && (
                      <span
                        className="ml-2 rounded bg-go/20 px-1.5 py-0.5 text-xs font-semibold text-go"
                        title="Top score on a finished night"
                      >
                        WON
                      </span>
                    )}
                    {p.removedAt && (
                      <span className="ml-2 text-xs text-stop">
                        removed{p.removedReason ? ` — ${p.removedReason}` : ''}
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-muted">
                    {p.eventName}
                    {p.eventScheduledFor
                      ? ` · ${new Date(p.eventScheduledFor).toLocaleDateString()}`
                      : ''}
                    {p.eventStatus !== 'ENDED' && (
                      <span className="ml-1 text-xs">({p.eventStatus.toLowerCase()})</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">{p.score}</td>
                  <td className="py-2 pr-3">
                    {p.ticket ? (
                      <>
                        <span className="font-mono">{p.ticket.code}</span>
                        <span className="ml-2 text-xs text-muted">
                          {p.ticket.source.toLowerCase()} · {p.ticket.status.toLowerCase()}
                        </span>
                      </>
                    ) : (
                      <span className="text-muted">free night</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {p.ticket ? formatPence(p.ticket.amountPence) : '—'}
                    {p.ticket?.refundedPence ? (
                      <span className="block text-xs text-stop">
                        −{formatPence(p.ticket.refundedPence)}
                      </span>
                    ) : null}
                  </td>
                  <td className="py-2 text-muted">
                    {new Date(p.joinedAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.pages > 1 && (
        <div className="mt-4 flex items-center justify-between gap-3 text-sm">
          <button
            disabled={data.page <= 1}
            onClick={() => setPage((n) => Math.max(1, n - 1))}
            className="rounded-lg bg-ink px-3 py-1.5 disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-muted">
            Page {data.page} of {data.pages}
          </span>
          <button
            disabled={data.page >= data.pages}
            onClick={() => setPage((n) => n + 1)}
            className="rounded-lg bg-ink px-3 py-1.5 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </section>
  );
}
